import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { libraryAsset } from "../../utils/libraryAssets";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../../components/auth/AuthModal";
import { getViewerAsset, postLibraryReaderAsk } from "../../services/libraryApi";
import {
  READER_STORYTELLER_IMAGE_FILE,
  READER_STORYTELLER_MODE_CHIPS,
  READER_STORYTELLER_TITLE,
  READER_STORYTELLER_INTRO,
  READER_STORYTELLER_PROMPTS,
} from "../../data/libraryMockData";
import { NOINDEX_PRIVATE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { isUsableDisplayImageUrl, resolveClientV2GenesisTokenCoverPng } from "../../utils/ipfsDisplay";
import LibraryComicFlipbook from "./LibraryComicFlipbook";
import LibraryReaderExpandOverlay from "./LibraryReaderExpandOverlay";
import "./library-reader.css";

const FALLBACK_COVER_PACK = "04_library_featured_display.png";
const FALLBACK_INTERIOR_PACK = "02_library_tomes_reader_background.png";

const REQUIRED_READER_COPY =
  "Sign in with an account that owns this title to open the reader.";
/** Short note above the three panels — avoid backend jargon on the reading surface. */
const READER_STAGE_NOTE =
  "Covers and pages load through your library access. Use the Lore Terminal for story questions.";

function publicUrlBase() {
  return typeof process.env.PUBLIC_URL === "string" ? process.env.PUBLIC_URL : "";
}

function resolveCoverImageUrl(asset) {
  if (!asset) return libraryAsset(FALLBACK_COVER_PACK);
  const c = (asset.coverImage && String(asset.coverImage).trim()) || "";
  const h = (asset.heroImage && String(asset.heroImage).trim()) || "";
  const pick = c || h;
  if (!pick) return libraryAsset(FALLBACK_COVER_PACK);
  if (/^https?:\/\//i.test(pick)) return pick;
  if (pick.startsWith("/")) return `${publicUrlBase()}${pick}`;
  return libraryAsset(pick);
}

export default function LibraryReaderPage() {
  const { assetSlug: rawSlug } = useParams();
  const [searchParams] = useSearchParams();
  const entitlementIdParam = useMemo(
    () => String(searchParams.get("entitlementId") || "").trim(),
    [searchParams]
  );
  const slug = useMemo(() => String(rawSlug || "").trim().toLowerCase(), [rawSlug]);

  const { loading: authLoading, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [readingPosition, setReadingPosition] = useState({ index: 0, pageNumber: 1, total: 0, mode: "" });
  const [chatTurns, setChatTurns] = useState([]);

  const [readerExpanded, setReaderExpanded] = useState(false);
  const [expandStartIdx, setExpandStartIdx] = useState(0);

  const [viewerLoading, setViewerLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [viewerError, setViewerError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !slug) {
      setPayload(null);
      setViewerLoading(false);
      setViewerError("");
      setForbidden(false);
      setNotFound(false);
      return undefined;
    }

    let cancelled = false;
    setViewerLoading(true);
    setViewerError("");
    setForbidden(false);
    setNotFound(false);
    setPayload(null);

    (async () => {
      try {
        const data = await getViewerAsset(slug, { entitlementId: entitlementIdParam || undefined });
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (cancelled) return;
        const status = e?.status;
        const msg = e?.message || "Something went wrong.";
        if (status === 403) {
          setForbidden(true);
          setViewerError(msg);
        } else if (status === 404) {
          setNotFound(true);
          setViewerError(msg);
        } else {
          setViewerError(msg);
        }
      } finally {
        if (!cancelled) setViewerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, slug, entitlementIdParam]);

  useEffect(() => {
    setChatTurns([]);
    setAiInput("");
    setAiError("");
    setReadingPosition({ index: 0, pageNumber: 1, total: 0, mode: "" });
    setReaderExpanded(false);
    setExpandStartIdx(0);
  }, [slug, entitlementIdParam]);

  const openReaderExpand = useCallback(() => {
    setExpandStartIdx(Math.max(0, Number(readingPosition.index) || 0));
    setReaderExpanded(true);
  }, [readingPosition.index]);

  const title = useMemo(() => {
    if (payload?.asset?.title) return payload.asset.title;
    return slug ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Library reader";
  }, [payload, slug]);

  const coverPaneLabel = useMemo(() => {
    const t = payload?.edition?.editionType;
    if (t === "digital") return "1 · Edition cover";
    return "1 · NFT / collectible cover";
  }, [payload]);

  const readerFiles = useMemo(() => {
    const list = Array.isArray(payload?.files) ? payload.files : [];
    const pdf = list.find((f) => f.fileType === "pdf" && f.readerUrl && f.readerContentAvailable);
    const pages = list
      .filter((f) => f.fileType === "page_image" && f.readerUrl && f.readerContentAvailable)
      .sort((a, b) => {
        const pa = Number(a.pageNumber);
        const pb = Number(b.pageNumber);
        const aOk = Number.isFinite(pa);
        const bOk = Number.isFinite(pb);
        if (aOk && bOk && pa !== pb) return pa - pb;
        if (aOk && !bOk) return -1;
        if (!aOk && bOk) return 1;
        const dn = String(a.displayName || "").localeCompare(String(b.displayName || ""), undefined, { numeric: true });
        if (dn !== 0) return dn;
        return String(a.id).localeCompare(String(b.id));
      });
    const epubs = list.filter((f) => f.fileType === "epub" && f.readerUrl);
    return { pdf, pages, epubs };
  }, [payload]);

  const isComicAsset = String(payload?.asset?.type || "").toLowerCase() === "comic";
  const useComicFlipbook =
    isComicAsset &&
    (readerFiles.pages.length > 0 || Boolean(readerFiles.pdf?.readerUrl));

  const fallbackInteriorBg = useMemo(() => {
    const list = Array.isArray(payload?.files) ? payload.files : [];
    const pub = list.find((f) => f.isPublicPreview && f.readerUrl);
    if (pub?.readerUrl) return pub.readerUrl;
    return libraryAsset(FALLBACK_INTERIOR_PACK);
  }, [payload]);

  const hasInteriorDelivery =
    Boolean(readerFiles.pdf) || readerFiles.pages.length > 0 || readerFiles.epubs.length > 0;

  const onReadingPageChange = useCallback((pos) => {
    if (!pos || typeof pos !== "object") return;
    setReadingPosition({
      index: typeof pos.index === "number" ? pos.index : 0,
      pageNumber: typeof pos.pageNumber === "number" && Number.isFinite(pos.pageNumber) ? pos.pageNumber : 1,
      total: typeof pos.total === "number" ? pos.total : 0,
      mode: String(pos.mode || ""),
    });
  }, []);

  const submitStory = useCallback(
    async (explicitQuestion, opts = {}) => {
      const q = String(explicitQuestion != null ? explicitQuestion : aiInput).trim();
      if (!q || !slug) return;
      setAiLoading(true);
      setAiError("");
      const pageNum =
        readingPosition.pageNumber != null && Number.isFinite(Number(readingPosition.pageNumber))
          ? Number(readingPosition.pageNumber)
          : null;
      try {
        const data = await postLibraryReaderAsk(slug, {
          question: q,
          currentPage: pageNum,
          mode: "spoiler_safe",
          ...(opts.experienceHint ? { experienceHint: opts.experienceHint } : {}),
          ...(entitlementIdParam ? { entitlementId: entitlementIdParam } : {}),
        });
        if (data?.ok && data.reply) {
          setAiError("");
          setChatTurns((prev) => [...prev, { role: "user", text: q }, { role: "assistant", text: data.reply }]);
        } else {
          setChatTurns((prev) => [...prev, { role: "user", text: q }]);
          setAiError(data?.message || "Storyteller could not answer right now.");
        }
      } catch (e) {
        setChatTurns((prev) => [...prev, { role: "user", text: q }]);
        const status = e?.status;
        if (status === 403) {
          setAiError("You need to own this title to use the Storyteller.");
        } else {
          setAiError(e?.message || "Storyteller connection failed. Please try again.");
        }
      } finally {
        setAiLoading(false);
      }
    },
    [aiInput, slug, readingPosition.pageNumber, entitlementIdParam]
  );

  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: `${title} · Reader | Treydora`,
    description: "Protected Treydora library reader.",
  });

  const storytellerImg = libraryAsset(READER_STORYTELLER_IMAGE_FILE);

  if (authLoading) {
    return (
      <div className="lread lread--gate">
        <p className="lread-gate__text">Checking your session…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="lread lread--gate">
        <div className="lread-gate__card">
          <h1 className="lread-gate__title serif">Sign in to open this reader</h1>
          <p className="lread-gate__lead">{REQUIRED_READER_COPY}</p>
          <button type="button" className="lread-gate__btn" onClick={() => setAuthOpen(true)}>
            Log in
          </button>
          <p className="lread-gate__links">
            <Link to="/library">Back to Library</Link>
            <span aria-hidden> · </span>
            <Link to="/dashboard/library">My Library</Link>
          </p>
        </div>
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      </div>
    );
  }

  if (!slug) {
    return (
      <div className="lread lread--gate">
        <p className="lread-gate__text">Missing library item.</p>
        <Link to="/library">Back to Library</Link>
      </div>
    );
  }

  if (viewerLoading) {
    return (
      <div className="lread lread--gate">
        <p className="lread-gate__text">Opening reader…</p>
      </div>
    );
  }

  if (forbidden || notFound || (viewerError && !payload)) {
    return (
      <div className="lread lread--gate">
        <div className="lread-gate__card">
          <h1 className="lread-gate__title serif">
            {forbidden
              ? "You do not have access to this library item."
              : notFound
                ? "Library asset not found."
                : "Reader unavailable"}
          </h1>
          {!forbidden ? (
            <p className="lread-gate__lead">{viewerError || "Unable to load this title."}</p>
          ) : (
            <p className="lread-gate__lead">{REQUIRED_READER_COPY}</p>
          )}
          <p className="lread-gate__hint">
            <Link to="/library">Library storefront</Link>
            <span aria-hidden> · </span>
            <Link to="/dashboard/library">My Library</Link>
          </p>
        </div>
      </div>
    );
  }

  if (!payload?.asset) {
    return (
      <div className="lread lread--gate">
        <p className="lread-gate__text">{viewerError || "Unable to load reader."}</p>
        <Link to="/library">Back to Library</Link>
      </div>
    );
  }

  const rawDisplayCover =
    typeof payload?.displayCoverUrl === "string" ? payload.displayCoverUrl.trim() : "";
  let coverUrl = isUsableDisplayImageUrl(rawDisplayCover) ? rawDisplayCover : "";
  if (!coverUrl && payload?.entitlement) {
    const e = payload.entitlement;
    coverUrl =
      resolveClientV2GenesisTokenCoverPng(
        {
          productType: e.productType,
          contractAddress: e.contractAddress,
          chainId: e.chainId,
          assignedTokenId: e.assignedTokenId,
        },
        e.tokenId
      ) || "";
  }
  if (!coverUrl) coverUrl = resolveCoverImageUrl(payload.asset);

  const canExpandReader =
    hasInteriorDelivery &&
    (Boolean(readerFiles.pdf?.readerUrl) || readerFiles.pages.length > 0);

  const showInlineReader = hasInteriorDelivery && !readerExpanded;

  return (
    <div className="lread">
      <header className="lread-topbar">
        <Link to="/dashboard/library" className="lread-back">
          ← Back to My Library
        </Link>
        <h1 className="lread-title serif">{title}</h1>
        <div className="lread-controls">
          <span className="lread-pill">Reader</span>
          {canExpandReader ? (
            <button
              type="button"
              className="lread-reading-expand lread-reading-expand--toolbar"
              onClick={openReaderExpand}
            >
              Expand reader
            </button>
          ) : (
            <button
              type="button"
              className="lread-reading-expand lread-reading-expand--toolbar"
              disabled
              title="Interior files are not available for this title yet."
            >
              Expand unavailable
            </button>
          )}
        </div>
      </header>

      <div className="lread-stage">
        <p className="lread-disclaimer">{READER_STAGE_NOTE}</p>

        <div className="lread-spread">
          <section className="lread-pane lread-pane--cover" aria-label="Cover">
            <span className="lread-pane__label">{coverPaneLabel}</span>
            <p className="lread-pane__hint">
              {payload.edition?.editionType === "nft"
                ? "Your entitlement’s collectible art — shown in full without cropping."
                : "Cover art for this digital edition — full image, letterboxed on a dark stage when needed."}
            </p>
            <div className="lread-cover-frame">
              <img className="lread-cover__img" src={coverUrl} alt={`${title} cover`} loading="lazy" />
            </div>
          </section>

          <section className="lread-pane lread-pane--center lread-pane--reading" aria-label="Interior content">
            <div className="lread-reading-head">
              <span className="lread-pane__label">2 · Comic / book content</span>
              {canExpandReader ? (
                <button type="button" className="lread-reading-expand" onClick={openReaderExpand}>
                  Expand reader
                </button>
              ) : null}
            </div>
            {!hasInteriorDelivery ? (
              <>
                <div
                  className="lread-pane__img lread-pane__img--interior"
                  style={{ backgroundImage: `url(${fallbackInteriorBg})` }}
                  role="img"
                  aria-hidden
                />
                <p className="lread-placeholder-caption">
                  {payload.files?.length
                    ? `${REQUIRED_READER_COPY} Protected files exist but no embedded URL was issued yet (configure S3 presign / local proxy).`
                    : `${REQUIRED_READER_COPY} No reader files uploaded for this asset yet.`}
                </p>
              </>
            ) : null}

            {readerExpanded ? (
              <p className="lread-reading-expanded-note">
                Expanded reader is active — close it to continue reading in this panel.
              </p>
            ) : null}

            {showInlineReader && useComicFlipbook ? (
              <LibraryComicFlipbook
                key={`reader-flip-${slug}`}
                pageImages={readerFiles.pages}
                pdfUrl={readerFiles.pages.length ? null : readerFiles.pdf?.readerUrl || null}
                title={title}
                onPageChange={onReadingPageChange}
                startImagePage={readingPosition.index}
                pdfInitialPageIndex={readingPosition.index}
              />
            ) : showInlineReader ? (
              <>
                {readerFiles.pdf?.readerUrl ? (
                  <div className="lread-pdf-shell">
                    <iframe className="lread-pdf-frame" title={`${title} — PDF`} src={readerFiles.pdf.readerUrl} />
                  </div>
                ) : null}

                {readerFiles.pages.length ? (
                  <div className="lread-pages" aria-label="Book pages">
                    {readerFiles.pages.map((pg) => (
                      <figure key={pg.id} className="lread-page">
                        <img
                          className="lread-page__img"
                          src={pg.readerUrl}
                          alt={pg.displayName || `Page ${pg.pageNumber ?? ""}`}
                          loading="lazy"
                        />
                        {pg.pageNumber != null ? (
                          <figcaption className="lread-page__cap">Page {pg.pageNumber}</figcaption>
                        ) : null}
                      </figure>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}

            {readerFiles.epubs.map((ep) => (
              <div key={ep.id} className="lread-epub-card">
                <p className="lread-epub-card__title">EPUB · {ep.displayName || "Book file"}</p>
                <p className="lread-epub-card__note">{ep.epubNote || "EPUB inline reader — TODO."}</p>
                <a className="lread-epub-card__link" href={ep.readerUrl} target="_blank" rel="noopener noreferrer">
                  Open / download EPUB (new tab)
                </a>
              </div>
            ))}

            {payload.files?.length ? (
              <p className="lread-file-hint">
                {payload.readerFilesReady
                  ? `Vault: ${payload.files.length} file(s) · delivery active for embeddable types.`
                  : `Vault: ${payload.files.length} file(s) — EPUB or placeholder rows may need follow-up.`}
              </p>
            ) : null}
          </section>

          <section className="lread-pane lread-pane--storyteller" aria-label="Lore Terminal">
            <span className="lread-pane__label lread-pane__label--sr-only">Panel 3 — Lore Terminal</span>
            <div className="lread-story lread-story--chat">
              <div className="lread-story__visual lread-story__visual--compact">
                <div className="lread-story__hero-frame">
                  <img
                    className="lread-story__hero-img"
                    src={storytellerImg}
                    alt=""
                    draggable={false}
                  />
                </div>
              </div>
              <div className="lread-story__body">
                <h2 className="lread-story__title serif">{READER_STORYTELLER_TITLE}</h2>
                <p className="lread-story__intro lread-story__intro--muted">
                  {aiLoading ? "One moment…" : READER_STORYTELLER_INTRO}
                </p>
                {readingPosition.total > 0 ? (
                  <p className="lread-story__pager-hint" aria-live="polite">
                    Page {readingPosition.pageNumber}
                    {readingPosition.total ? ` of ${readingPosition.total}` : ""}
                  </p>
                ) : null}

                <div className="lread-chat__composer">
                  <label className="lread-visually-hidden" htmlFor="lread-story-textarea">
                    Your question
                  </label>
                  <textarea
                    id="lread-story-textarea"
                    className="lread-story__textarea"
                    placeholder="Ask the Lorekeeper about Koraxos, Oshar, the conflict, or the shadows beneath this chapter…"
                    rows={2}
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void submitStory();
                      }
                    }}
                    disabled={aiLoading}
                  />
                  <button
                    type="button"
                    className="lread-story__send"
                    disabled={aiLoading || !String(aiInput || "").trim()}
                    onClick={() => void submitStory()}
                  >
                    {aiLoading ? "Sending…" : "Send"}
                  </button>
                </div>

                <div className="lread-chat__log" role="log" aria-relevant="additions" aria-live="polite">
                  {!chatTurns.length && !aiError ? (
                    <p className="lread-chat__empty">Replies appear here.</p>
                  ) : null}
                  {chatTurns.map((row, i) => (
                    <div
                      key={`${i}-${row.role}`}
                      className={row.role === "user" ? "lread-chat__bubble lread-chat__bubble--user" : "lread-chat__bubble lread-chat__bubble--ai"}
                    >
                      <span className="lread-chat__bubble-label">{row.role === "user" ? "You" : "Lore"}</span>
                      <p className="lread-chat__bubble-text">{row.text}</p>
                    </div>
                  ))}
                  {aiLoading ? (
                    <div className="lread-chat__bubble lread-chat__bubble--ai lread-chat__bubble--pending">
                      <span className="lread-chat__bubble-label">Lore</span>
                      <p className="lread-chat__bubble-text">…</p>
                    </div>
                  ) : null}
                </div>

                {aiError ? (
                  <p className="lread-story__error" role="alert">
                    {aiError}
                  </p>
                ) : null}

                <div className="lread-story__chips lread-story__chips--minimal" role="group" aria-label="Suggested questions">
                  {READER_STORYTELLER_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="lread-story__chip"
                      disabled={aiLoading}
                      onClick={() => {
                        setAiInput(p);
                        void submitStory(p);
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div
                  className="lread-story__chips lread-story__chips--minimal lread-story__chips--modes"
                  role="group"
                  aria-label="Quick response style"
                >
                  {READER_STORYTELLER_MODE_CHIPS.map((c) => (
                    <button
                      key={c.hint}
                      type="button"
                      className="lread-story__chip"
                      disabled={aiLoading}
                      onClick={() => {
                        setAiInput(c.prompt);
                        void submitStory(c.prompt, { experienceHint: c.hint });
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <LibraryReaderExpandOverlay
        open={readerExpanded}
        onClose={() => setReaderExpanded(false)}
        title={title}
        pdfUrl={readerFiles.pages.length ? null : readerFiles.pdf?.readerUrl || null}
        pageImages={readerFiles.pages}
        startIndex={expandStartIdx}
        onPageChange={onReadingPageChange}
      />
    </div>
  );
}
